import { useCallback } from 'react'
import { onlineManager, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { filterInventory } from '@mads/shared'
import type {
  EquipmentItem,
  EquipmentItemInput,
  FixtureModelInput,
  InventoryFilters,
  Location,
  LocationInput,
  PasswordChangeInput,
  UserInput,
} from '@mads/shared'
import { api, useCurrentUser } from './session'
import { createPasskey } from './passkeys'
import { useQueueStore } from './queue'
import {
  queueFixtureModelCreate,
  queueFixtureModelDelete,
  queueFixtureModelPhoto,
  queueFixtureModelUpdate,
  queueItemPhoto,
  queueItemCreate,
  queueItemDelete,
  queueItemUpdate,
  queueLocationCreate,
  queueLocationDelete,
  queueLineCount,
  queueLocationReorder,
  queueLocationUpdate,
  queueStocktakeAbandon,
  queueStocktakeComplete,
  queueStocktakeStart,
} from './offlineWrites'
import { inOrderOf } from './walkingOrder'
import { nextMadsCode } from './madsCode'

/**
 * Whether a write should go to the API or into the queue (issue #64, step 2).
 *
 * "Online" is read from the query layer's own `onlineManager` so the decision,
 * the paused queries and the chip in the shell can never disagree — one notion
 * of it, and everything reads it from there.
 *
 * **A non-empty queue is the other half** (issue #70). This used to be
 * connectivity alone, which left a window: a sync that failed and an error the
 * walker dismissed — a 429, a 403, a resolver they backed out of — leaves the
 * app online holding changes, and the next edit went straight to
 * `PATCH /api/items/{id}`. It landed *before* the older queued edit, which then
 * replayed on top and reverted it. So while there is anything queued, everything
 * queues behind it: order is then preserved by construction and there is no way
 * round the queue.
 */
export function shouldQueue(): boolean {
  return !onlineManager.isOnline() || useQueueStore.getState().changesets.length > 0
}

export const queryKeys = {
  dashboard: ['dashboard'] as const,
  /**
   * The whole live inventory, under one key (issue #70).
   *
   * It used to be `['items', filters]` — one cache entry per filter combination,
   * which meant the same records written to IndexedDB several times over and,
   * worse, a filter set nobody had visited being a filter set nobody had
   * offline. The endpoint is unpaginated, the walk needs all of it anyway, and
   * `useItems` narrows it here on the device.
   */
  items: ['items'] as const,
  item: (id: number) => ['item', id] as const,
  trash: ['trash'] as const,
  autocomplete: ['autocomplete'] as const,
  auditLog: (itemId: number) => ['auditLog', itemId] as const,
  fixtureModels: ['fixtureModels'] as const,
  locations: ['locations'] as const,
  locationTrash: ['locationTrash'] as const,
  users: ['users'] as const,
  user: (id: number) => ['user', id] as const,
  userAuditLog: (id: number) => ['userAuditLog', id] as const,
  myAuditLog: ['myAuditLog'] as const,
  stocktakes: ['stocktakes'] as const,
  stocktake: (id: number) => ['stocktake', id] as const,
  authProviders: ['authProviders'] as const,
  passkeys: ['passkeys'] as const,
  twoFactor: ['twoFactor'] as const,
}

/**
 * Whether the sign-in screen can offer "Continue with Google" (issue #13).
 * Deployment configuration, not data — it cannot change while the screen is
 * open, so it is fetched once and never refetched.
 */
export function useAuthProviders() {
  return useQuery({
    queryKey: queryKeys.authProviders,
    queryFn: () => api.providers(),
    staleTime: Infinity,
  })
}

export function useDashboard() {
  return useQuery({ queryKey: queryKeys.dashboard, queryFn: () => api.dashboard() })
}

/**
 * The inventory, filtered (spec W-02).
 *
 * One fetch of everything live, narrowed with `select` — see `queryKeys.items`
 * for why, and `matchesInventoryFilters` in `@mads/shared` for the predicate,
 * which mirrors what MySQL would have done. `select` runs against the cached
 * data rather than re-fetching, so changing a filter is instant and works
 * underground.
 *
 * The API's own query parameters are untouched and still supported; what
 * changed is that this client stops using them.
 */
export function useItems(filters: InventoryFilters) {
  const { q, category, locationId, fixtureModelId } = filters

  // Memoised on the filter *values*, not on the object: the route rebuilds
  // `filters` on every render, and an unstable `select` makes TanStack Query
  // recompute — and hand back a new array — every time, which re-renders the
  // whole table for nothing.
  const select = useCallback(
    (items: EquipmentItem[]) => filterInventory(items, { q, category, locationId, fixtureModelId }),
    [q, category, locationId, fixtureModelId],
  )

  return useQuery({ queryKey: queryKeys.items, queryFn: () => api.items(), select })
}

/**
 * One item, seeded from the inventory list when its own screen has never been
 * fetched (issue #64).
 *
 * Without this, offline stops at the list: every row is there, and clicking one
 * gives "this screen has not been saved" — which is the screen the edit form
 * lives on, so a walk round a cellar could look at kit and change none of it.
 * The list already carries the whole record (`InventoryPresenter` builds one
 * shape), so there is nothing to fetch that is not already on the device.
 *
 * `placeholderData` rather than `initialData`, deliberately: the seed is shown
 * immediately but never written into this query's cache entry, so online it is
 * a faster first paint that the real fetch replaces, and it can never become a
 * stale copy of a record the list has since dropped.
 */
export function useItem(id: number) {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: queryKeys.item(id),
    queryFn: () => api.item(id),
    // A record made with no connection has a provisional, negative id and there
    // is nothing on the server to fetch under it (issue #70) — the copy the
    // queue wrote is the whole record until sync hands back a real one. Asking
    // anyway would 404 over a lantern somebody is in the middle of correcting.
    // The same guard `useStocktake` has, for the same reason.
    enabled: id > 0,
    placeholderData: () => itemFromList(queryClient, id),
  })
}

function itemFromList(queryClient: ReturnType<typeof useQueryClient>, id: number) {
  return queryClient.getQueryData<EquipmentItem[]>(queryKeys.items)?.find((item) => item.id === id)
}

/** Fresh reads include the trash so a later restore cannot reuse a suggested label. */
export function useSuggestMadsCode() {
  const queryClient = useQueryClient()

  return useMutation({
    networkMode: 'always',
    mutationFn: async () => {
      if (!onlineManager.isOnline()) throw new Error('Connect to find an unused code.')
      const [items, trash] = await Promise.all([api.items(), api.trash()])
      // Read after the requests: queued captures made while waiting count too.
      const local = queryClient.getQueryData<EquipmentItem[]>(queryKeys.items) ?? []
      return nextMadsCode([...items, ...trash, ...local].map((item) => item.madsCode))
    },
  })
}

export function useAutocomplete() {
  return useQuery({ queryKey: queryKeys.autocomplete, queryFn: () => api.autocomplete() })
}

export function useTrash() {
  return useQuery({ queryKey: queryKeys.trash, queryFn: () => api.trash() })
}

export function useAuditLog(itemId: number) {
  return useQuery({ queryKey: queryKeys.auditLog(itemId), queryFn: () => api.auditLog(itemId) })
}

export function useFixtureModels() {
  return useQuery({ queryKey: queryKeys.fixtureModels, queryFn: () => api.fixtureModels() })
}

/**
 * One fixture model, read out of the list every screen already shares — the
 * same trade `useLocation` makes: there are few of them, the list is
 * invalidated by every mutation, and a second cache entry could only disagree
 * with the first. A missing id comes back as `null`, not an error.
 */
export function useFixtureModel(id: number) {
  return useQuery({
    queryKey: queryKeys.fixtureModels,
    queryFn: () => api.fixtureModels(),
    select: (models) => models.find((model) => model.id === id) ?? null,
  })
}

export function useLocations() {
  return useQuery({ queryKey: queryKeys.locations, queryFn: () => api.locations() })
}

/**
 * One location, read out of the list every screen already shares. Locations are
 * few and the list is invalidated by every mutation, so a dedicated endpoint
 * would buy nothing but a second cache entry to disagree with the first.
 * Missing ids come back as `null` rather than throwing — the edit page says so.
 */
export function useLocation(id: number) {
  return useQuery({
    queryKey: queryKeys.locations,
    queryFn: () => api.locations(),
    select: (locations) => locations.find((location) => location.id === id) ?? null,
  })
}

export function useUsers(enabled: boolean) {
  return useQuery({ queryKey: queryKeys.users, queryFn: () => api.users(), enabled })
}

export function useUser(id: number, enabled: boolean) {
  return useQuery({ queryKey: queryKeys.user(id), queryFn: () => api.user(id), enabled })
}

/**
 * One account's history, for an admin investigating it (issue #58). Gated on
 * the same `enabled` the record itself is, so a crew member's screen never
 * makes the call that would 403.
 */
export function useUserAuditLog(id: number, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.userAuditLog(id),
    queryFn: () => api.userAuditLog(id),
    enabled,
  })
}

/** Your own history, on `/profile` (issue #58). */
export function useMyAuditLog() {
  return useQuery({ queryKey: queryKeys.myAuditLog, queryFn: () => api.myAuditLog() })
}

export function useStocktakes() {
  return useQuery({ queryKey: queryKeys.stocktakes, queryFn: () => api.stocktakes() })
}

export function useStocktake(id: number | null) {
  return useQuery({
    queryKey: queryKeys.stocktake(id ?? 0),
    queryFn: () => api.stocktake(id as number),
    // A walk started with no connection has a provisional, negative id (issue
    // #64, step 3), and there is nothing on the server to fetch under it — so
    // the cached copy the queue wrote is the whole record until sync hands back
    // a real one. Asking anyway would 404 over a walk somebody is mid-way
    // through.
    enabled: id !== null && id > 0,
  })
}

/**
 * Anything that changes the kit invalidates the inventory, the dashboard
 * counters, the per-location totals and both trashes together — they are the
 * same data counted differently, and a delete or a restore moves a record
 * between the live views and a trash. Binning a location reaches further than
 * it looks: the kit still on it starts reading as unassigned, so the item list
 * and the dashboard change without any item being touched (issue #9).
 */
function useInventoryInvalidator() {
  const queryClient = useQueryClient()

  return async () => {
    // Nothing to refetch from with no connection, and the caches have just been
    // patched with what the queued change did (issue #64) — invalidating would
    // replace that with a paused query and a spinner.
    if (shouldQueue()) return

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.items }),
      queryClient.invalidateQueries({ queryKey: ['item'] }),
      // Every item mutation is also an audit-log event (created/updated/
      // deleted/restored/purged), so the change-history panel on the detail
      // screen would otherwise stay stale until a navigation refetches it.
      queryClient.invalidateQueries({ queryKey: ['auditLog'] }),
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard }),
      queryClient.invalidateQueries({ queryKey: queryKeys.locations }),
      queryClient.invalidateQueries({ queryKey: queryKeys.fixtureModels }),
      queryClient.invalidateQueries({ queryKey: queryKeys.autocomplete }),
      queryClient.invalidateQueries({ queryKey: queryKeys.trash }),
      queryClient.invalidateQueries({ queryKey: queryKeys.locationTrash }),
    ])
  }
}

export function useCreateItem() {
  const queryClient = useQueryClient()
  const invalidate = useInventoryInvalidator()

  return useMutation({
    mutationFn: (input: EquipmentItemInput) =>
      shouldQueue() ? queueItemCreate(queryClient, input) : api.createItem(input),
    onSuccess: invalidate,
  })
}

export function useUpdateItem(id: number) {
  const queryClient = useQueryClient()
  const invalidate = useInventoryInvalidator()

  return useMutation({
    mutationFn: (input: Partial<EquipmentItemInput>) =>
      shouldQueue() ? queueItemUpdate(queryClient, id, input) : api.updateItem(id, input),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.item(id), updated)
      await invalidate()
    },
  })
}

export function useDeleteItem() {
  const queryClient = useQueryClient()
  const invalidate = useInventoryInvalidator()

  return useMutation({
    mutationFn: (id: number) =>
      shouldQueue() ? queueItemDelete(queryClient, id) : api.deleteItem(id),
    onSuccess: invalidate,
  })
}

/**
 * Photographs (issue #56). Adding, replacing and removing all answer with the
 * item as it now reads, so the cache is written from the response rather than
 * refetched — the same shape `useUpdateItem` has, because on the server these
 * are the same event: a field on the record changing.
 *
 * `null` removes the item's own photograph and drops it back to its fixture
 * model's. It never reaches through to the model's picture, which every other
 * lantern of that kind is also using.
 */
export function useItemPhoto(id: number) {
  const queryClient = useQueryClient()
  const invalidate = useInventoryInvalidator()

  return useMutation({
    mutationFn: (file: File | null) =>
      shouldQueue()
        ? queueItemPhoto(queryClient, id, file)
        : file === null
          ? api.removeItemImage(id)
          : api.setItemImage(id, file),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.item(id), updated)
      await invalidate()
    },
  })
}

/**
 * The catalogue shot on a fixture model. Removing it leaves every lantern
 * showing it with none — unlike binning the model, which copies it down to
 * them first.
 */
export function useFixtureModelPhoto(id: number) {
  const invalidate = useInventoryInvalidator()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (file: File | null) =>
      shouldQueue()
        ? queueFixtureModelPhoto(queryClient, id, file)
        : file === null
          ? api.removeFixtureModelImage(id)
          : api.setFixtureModelImage(id, file),
    onSuccess: invalidate,
  })
}

/**
 * Taking an item back out of the trash (issue #8).
 *
 * Deliberately *not* offline-capable: the trash is not one of the screens kept
 * on the device (issue #64), so there is nothing there to restore from in a
 * cellar — and queueing a restore of a record the walker cannot see would be
 * queueing a guess.
 */
export function useRestoreItem() {
  const invalidate = useInventoryInvalidator()

  return useMutation({
    mutationFn: (id: number) => api.restoreItem(id),
    onSuccess: invalidate,
  })
}

export function usePurgeItem() {
  const invalidate = useInventoryInvalidator()

  return useMutation({
    mutationFn: (id: number) => api.purgeItem(id),
    onSuccess: invalidate,
  })
}

export function useEmptyTrash() {
  const invalidate = useInventoryInvalidator()

  return useMutation({
    mutationFn: () => api.emptyTrash(),
    onSuccess: invalidate,
  })
}

/**
 * Fixture models ride the inventory invalidator: an item's optics are read
 * through its model (issue #30), so changing one changes how every lantern
 * linked to it reads.
 */
export function useCreateFixtureModel() {
  const queryClient = useQueryClient()
  const invalidate = useInventoryInvalidator()

  return useMutation({
    mutationFn: (input: FixtureModelInput) =>
      shouldQueue() ? queueFixtureModelCreate(queryClient, input) : api.createFixtureModel(input),
    onSuccess: invalidate,
  })
}

export function useUpdateFixtureModel() {
  const queryClient = useQueryClient()
  const invalidate = useInventoryInvalidator()

  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<FixtureModelInput> }) =>
      shouldQueue()
        ? queueFixtureModelUpdate(queryClient, id, input)
        : api.updateFixtureModel(id, input),
    onSuccess: invalidate,
  })
}

export function useDeleteFixtureModel() {
  const queryClient = useQueryClient()
  const invalidate = useInventoryInvalidator()

  return useMutation({
    mutationFn: (id: number) =>
      shouldQueue() ? queueFixtureModelDelete(queryClient, id) : api.deleteFixtureModel(id),
    onSuccess: invalidate,
  })
}

export function useCreateLocation() {
  const queryClient = useQueryClient()
  const invalidate = useInventoryInvalidator()

  return useMutation({
    mutationFn: (input: LocationInput) =>
      shouldQueue() ? queueLocationCreate(queryClient, input) : api.createLocation(input),
    onSuccess: invalidate,
  })
}

export function useUpdateLocation() {
  const queryClient = useQueryClient()
  const invalidate = useInventoryInvalidator()

  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<LocationInput> }) =>
      shouldQueue() ? queueLocationUpdate(queryClient, id, input) : api.updateLocation(id, input),
    onSuccess: invalidate,
  })
}

export function useDeleteLocation() {
  const queryClient = useQueryClient()
  const invalidate = useInventoryInvalidator()

  return useMutation({
    mutationFn: (id: number) =>
      shouldQueue() ? queueLocationDelete(queryClient, id) : api.deleteLocation(id),
    onSuccess: invalidate,
  })
}

/** The location trash is admin-only, so the query waits for that to be known. */
export function useLocationTrash(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.locationTrash,
    queryFn: () => api.locationTrash(),
    enabled,
  })
}

export function useRestoreLocation() {
  const invalidate = useInventoryInvalidator()

  return useMutation({
    mutationFn: (id: number) => api.restoreLocation(id),
    onSuccess: invalidate,
  })
}

export function usePurgeLocation() {
  const invalidate = useInventoryInvalidator()

  return useMutation({
    mutationFn: (id: number) => api.purgeLocation(id),
    onSuccess: invalidate,
  })
}

export function useEmptyLocationTrash() {
  const invalidate = useInventoryInvalidator()

  return useMutation({
    mutationFn: () => api.emptyLocationTrash(),
    onSuccess: invalidate,
  })
}

/**
 * Drag-to-reorder on the Locations screen (issue #27). The card has to stay
 * where it was dropped, so the cache is rewritten before the request goes —
 * a failure puts the old order back and says so. Only the locations list is
 * touched: an order is not a count, so nothing else on screen has changed.
 */
export function useReorderLocations() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: number[]) =>
      shouldQueue() ? queueLocationReorder(queryClient, ids) : api.reorderLocations(ids),
    onMutate: async (ids: number[]) => {
      // An in-flight refetch would otherwise land on top of the new order.
      await queryClient.cancelQueries({ queryKey: queryKeys.locations })

      const previous = queryClient.getQueryData<Location[]>(queryKeys.locations)
      if (previous !== undefined) {
        queryClient.setQueryData(queryKeys.locations, inOrderOf(previous, ids))
      }

      return { previous }
    },
    onError: (_error, _ids, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKeys.locations, context.previous)
      }
    },
    onSuccess: (locations) => queryClient.setQueryData(queryKeys.locations, locations),
  })
}

function useUserInvalidator() {
  const queryClient = useQueryClient()

  return async () => {
    if (shouldQueue()) return

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.users }),
      queryClient.invalidateQueries({ queryKey: ['user'] }),
      // Every account mutation is an audit entry too (issue #58), so the
      // history panel on the edit screen would otherwise stay stale until a
      // navigation refetched it — the same reason items invalidate theirs.
      queryClient.invalidateQueries({ queryKey: ['userAuditLog'] }),
    ])
  }
}

export function useCreateUser() {
  const invalidate = useUserInvalidator()
  return useMutation({ mutationFn: (input: UserInput) => api.createUser(input), onSuccess: invalidate })
}

export function useUpdateUser() {
  const invalidate = useUserInvalidator()
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<UserInput> }) =>
      api.updateUser(id, input),
    onSuccess: invalidate,
  })
}

export function useDeleteUser() {
  const invalidate = useUserInvalidator()
  return useMutation({ mutationFn: (id: number) => api.deleteUser(id), onSuccess: invalidate })
}

export function useResendInvite() {
  const invalidate = useUserInvalidator()
  return useMutation({ mutationFn: (id: number) => api.resendInvite(id), onSuccess: invalidate })
}

/**
 * Your own account (issue #52) is read, not written: nothing about the record
 * — the name included — is self-service, so there is no profile mutation here
 * and the store's copy only ever changes when the session is reloaded. What
 * the account holder changes for themselves is below.
 */
/**
 * Everything on the profile screen that changes what signs somebody in also
 * writes a row in their own history (issue #58), so the two go together — a
 * password just changed with no sign of it below is exactly the sort of thing
 * that makes somebody distrust the log.
 */
async function invalidateAccountSecurity(queryClient: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.twoFactor }),
    queryClient.invalidateQueries({ queryKey: queryKeys.myAuditLog }),
  ])
}

/**
 * Changing a password also untrusts every device server-side (issue #16), so
 * the two-step section is refetched — otherwise it would go on claiming a
 * trusted browser count that the API has just zeroed.
 */
export function useChangePassword() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: PasswordChangeInput) => api.changePassword(input),
    onSuccess: () => invalidateAccountSecurity(queryClient),
  })
}

/**
 * The passkeys on your own account (issue #15). There is no "2FA is on" flag
 * anywhere — having any of these is what that means — so this list is the one
 * thing the profile screen reads to decide what to say.
 */
export function usePasskeys() {
  return useQuery({ queryKey: queryKeys.passkeys, queryFn: () => api.passkeys() })
}

/**
 * Adding one is two round trips with a browser prompt in the middle: ask for a
 * challenge, have the authenticator sign it, hand back what it made. All three
 * are one mutation because they are one action to the person doing it — a
 * failure at any step leaves nothing registered and one message to read.
 */
export function useRegisterPasskey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ currentPassword, label }: { currentPassword: string; label: string }) => {
      const challenge = await api.passkeyRegistrationChallenge(currentPassword)
      const credential = await createPasskey(challenge.publicKey)

      return api.registerPasskey({
        challengeToken: challenge.challengeToken,
        label,
        credential,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.passkeys })
      await invalidateAccountSecurity(queryClient)
    },
  })
}

export function useRemovePasskey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, currentPassword }: { id: number; currentPassword: string }) =>
      api.removePasskey(id, currentPassword),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.passkeys })
      await invalidateAccountSecurity(queryClient)
    },
  })
}

/**
 * What second factor this account has, and how many browsers are allowed to
 * skip the texted half of it (issue #16).
 *
 * Not a setting anywhere: whether someone is texted follows from a mobile
 * number on an installation with Twilio switched on, so this is the API working
 * it out rather than a flag being read back.
 */
export function useTwoFactor() {
  return useQuery({ queryKey: queryKeys.twoFactor, queryFn: () => api.twoFactor() })
}

export function useForgetTrustedDevices() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (currentPassword: string) => api.forgetTrustedDevices(currentPassword),
    onSuccess: () => invalidateAccountSecurity(queryClient),
  })
}

export function useRequestPasswordReset() {
  return useMutation({ mutationFn: (email: string) => api.requestPasswordReset(email) })
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      api.resetPassword(token, password),
  })
}

function useStocktakeInvalidator() {
  const queryClient = useQueryClient()

  return async () => {
    if (shouldQueue()) return

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.stocktakes }),
      queryClient.invalidateQueries({ queryKey: ['stocktake'] }),
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard }),
      queryClient.invalidateQueries({ queryKey: queryKeys.items }),
    ])
  }
}

export function useStartStocktake() {
  const invalidate = useStocktakeInvalidator()
  const queryClient = useQueryClient()
  // Only for the provisional record's "started by", which the screen shows
  // before the server has ever heard of this walk.
  const name = useCurrentUser()?.name ?? null

  return useMutation({
    mutationFn: (locationIds: number[]) =>
      shouldQueue()
        ? queueStocktakeStart(queryClient, locationIds, name)
        : api.startStocktake(locationIds),
    onSuccess: invalidate,
  })
}

/**
 * Records one line's count and note.
 *
 * The item rides along beside the line id because that is what names the line
 * to the queue (issue #64, step 3): line ids come from `start()`, so a walk
 * begun in a cellar has none, and the item is the half of the pair the device
 * always knows.
 */
export function useCountLine(stocktakeId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      lineId,
      itemId,
      expectedQuantity,
      countedQuantity,
      note,
    }: {
      lineId: number
      itemId: number
      expectedQuantity: number
      countedQuantity?: number | null
      note?: string | null
    }) =>
      shouldQueue()
        ? queueLineCount(queryClient, stocktakeId, {
            itemId,
            expectedQuantity,
            ...(countedQuantity === undefined ? {} : { countedQuantity }),
            ...(note === undefined ? {} : { note }),
          })
        : api.countLine(stocktakeId, lineId, { countedQuantity, note }),
    onSuccess: async () => {
      if (shouldQueue()) return

      await queryClient.invalidateQueries({ queryKey: queryKeys.stocktake(stocktakeId) })
    },
  })
}

export function useCompleteStocktake() {
  const invalidate = useStocktakeInvalidator()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, applyCounts }: { id: number; applyCounts: boolean }) =>
      shouldQueue()
        ? queueStocktakeComplete(queryClient, id, applyCounts)
        : api.completeStocktake(id, applyCounts),
    onSuccess: invalidate,
  })
}

export function useAbandonStocktake() {
  const invalidate = useStocktakeInvalidator()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) =>
      shouldQueue() ? queueStocktakeAbandon(queryClient, id) : api.abandonStocktake(id),
    onSuccess: invalidate,
  })
}
